import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError, uploadFile } from "../lib/api";
import useModules from "../hooks/useModules";
import { formatDate } from "../utils/date";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "./ui/collapsible";
import { ChevronDown, Landmark } from "lucide-react";
import { toast } from "sonner";
import Avatar from "./Avatar";
import DateInput from "./DateInput";

const EMPTY = {
    full_name: "",
    photo_url: "",
    gender: "",
    date_of_birth: "",
    phone: "",
    email: "",
    national_id: "",
    module_employee_id: "",
    biometric_user_id: "",
    address: "",
    module_id: "",
    worksite: "",
    job_title: "",
    employment_type: "casual",
    worker_subtype: "",
    status: "active",
    // Iter60.p59 — Employment lifecycle. Defaults to `active` for new
    // hires; existing rows are hydrated from the server via `_iter54_enrich`.
    employment_status: "active",
    status_note: "",
    hire_date: "",
    probation_end_date: "",
    // Iter54 — Government documents & company ID card.
    cid_reference: "",
    cid_date_of_issue: "",
    nid_reference: "",
    nid_date_of_issue: "",
    nid_expiry_date: "",
    asl_id_card_issue_date: "",
    asl_id_card_expiry_date: "",
    // Iter60.p12 — Bank account details (optional group).
    bank_name: "",
    bank_account_number: "",
    bank_account_holder_name: "",
    bank_branch: "",
    bank_swift_code: "",
    bank_notes: "",
};

// Iter18 — Optional pay rate at create time (IC Camp only).
// Iter44 — Also available on Edit for IC employees; a blank Amount keeps the
// existing rate untouched, a non-zero Amount adds a NEW rate row (old rate
// stays in history for audit).
const PAY_RATE_EMPTY = { amount: "", frequency: "", effective_date: "" };

export default function EmployeeFormDialog({ open, onOpenChange, employee, onSaved }) {
    const { modules, byId: modulesById } = useModules();
    const { moduleCode: pathModuleCode } = useParams();
    const [form, setForm] = useState(EMPTY);
    // Iter65 Phase A — snapshot of the row as loaded so we can diff on
    // Save and only PATCH the fields the user ACTUALLY touched.
    // Untouched-empty fields are NEVER sent as `null` anymore — the
    // production data-loss root cause.
    const initialFormRef = React.useRef(EMPTY);
    const [payRate, setPayRate] = useState(PAY_RATE_EMPTY);
    const [submitting, setSubmitting] = useState(false);
    // Iter65 Phase A — Bank-clear confirmation flow: gate on typed-then-
    // deleted bank inputs. Only shown when the user actively cleared a
    // populated bank field (not on first-time empty).
    const [bankClearConfirm, setBankClearConfirm] = useState({
        open: false, confirmText: "", pending: null,
    });
    const [currentRate, setCurrentRate] = useState(null); // Iter44 — effective rate for the employee being edited
    const [currentRateLoading, setCurrentRateLoading] = useState(false);
    const isEdit = !!employee?.id;

    // Only expose the Pay Rate section when the currently-selected module is
    // IC Camp. Iter44 — available on both Add and Edit (was create-only). On
    // Edit we show a small "Current rate" block at the top of the section and
    // let the user add a new rate that will supersede the current one starting
    // from its effective_date. The old rate remains untouched for history.
    // Iter54 — Pay Rate section shown for any scoped module (IC/AP/CN),
    // matching the wage_rates parity fix from Iter52d.
    const showPayRate = !!form.module_id;

    useEffect(() => {
        if (!open) return;
        if (employee) {
            const seeded = { ...EMPTY, ...employee, worker_subtype: employee.worker_subtype || "" };
            setForm(seeded);
            // Iter65 Phase A — record the exact shape used to seed the
            // form; diff against this on Save so we only PATCH fields
            // the user actually changed.
            initialFormRef.current = seeded;
        } else {
            // Auto-preselect module based on the URL when creating from a
            // module-scoped Staff page (e.g. /m/ic/staff → IC).
            const urlCode = (pathModuleCode || "").toUpperCase();
            const preselected = urlCode ? modules.find((m) => (m.code || "").toUpperCase() === urlCode) : null;
            const seeded = { ...EMPTY, module_id: preselected?.id || "" };
            setForm(seeded);
            initialFormRef.current = seeded;
        }
        setPayRate(PAY_RATE_EMPTY);
    }, [open, employee, pathModuleCode, modules]);

    // Auto-adjust default frequency when the worker type flips: casual → daily,
    // non_casual → monthly. Only nudges the default; user can still change.
    useEffect(() => {
        if (!showPayRate) return;
        setPayRate((p) => {
            if (p.amount) return p;  // don't stomp user's edits once they've started typing
            return { ...p, frequency: form.employment_type === "casual" ? "daily" : "monthly" };
        });
    }, [form.employment_type, showPayRate]);

    // Default effective_date follows the hire_date field.
    useEffect(() => {
        if (!showPayRate) return;
        setPayRate((p) => (p.effective_date ? p : { ...p, effective_date: form.hire_date || "" }));
    }, [form.hire_date, showPayRate]);

    // Iter44 — In Edit mode, fetch the current effective rate for the employee
    // so we can display "Current: $X / day · Person-specific · effective from …"
    // above the input row. Skips if no employee, not IC, or dialog closed.
    useEffect(() => {
        if (!open) return;
        if (!isEdit || !showPayRate || !employee?.id) {
            setCurrentRate(null);
            return;
        }
        let cancelled = false;
        setCurrentRateLoading(true);
        const today = new Date().toISOString().slice(0, 10);
        api.get(`/employees/${employee.id}/effective-rate`, { params: { date: today } })
            .then(({ data }) => { if (!cancelled) setCurrentRate(data); })
            .catch(() => { if (!cancelled) setCurrentRate(null); })
            .finally(() => { if (!cancelled) setCurrentRateLoading(false); });
        return () => { cancelled = true; };
    }, [open, isEdit, showPayRate, employee?.id]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onPhotoChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const { photo_url } = await uploadFile("/employees/upload-photo", file);
            set("photo_url", photo_url);
            toast.success("Photo uploaded");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    // Iter18/Iter44 — Optional pay rate side-effect (IC-only). POSTs a new
    // wage_rate for this employee if the user supplied a non-zero amount.
    // Returns a human-readable extra message to append to the success toast,
    // or "" when nothing was posted.
    const _maybeCreatePayRate = async (employeeId, employeePayload) => {
        const amt = Number(payRate.amount);
        if (!showPayRate || !(amt > 0)) return "";
        const subtype = employeePayload.worker_subtype || "manual";
        const category = employeePayload.employment_type === "casual"
            ? "casual"
            : `non_casual_${subtype}`;
        const freq = payRate.frequency || (employeePayload.employment_type === "casual" ? "daily" : "monthly");
        const effective = payRate.effective_date || employeePayload.hire_date || new Date().toISOString().slice(0, 10);
        const ratePayload = {
            module_id: employeePayload.module_id,
            employee_id: employeeId,
            job_role: employeePayload.job_title,
            worker_category: category,
            payment_frequency: freq,
            currency: "USD",
            effective_date: effective,
            hourly_rate: freq === "hourly" ? amt : null,
            daily_rate: freq === "daily" ? amt : null,
            monthly_rate: freq === "monthly" ? amt : null,
        };
        await api.post("/wage-rates", ratePayload);
        const freqLabel = freq === "monthly" ? "month" : freq === "hourly" ? "hour" : "day";
        return `New pay rate $${amt.toFixed(2)}/${freqLabel} starting ${formatDate(effective)}.`;
    };

    // Iter65 Phase A — Bank field vocabulary shared between the diff
    // builder and the confirmation dialog.
    const _BANK_FIELDS = React.useMemo(() => [
        "bank_name", "bank_account_number", "bank_account_holder_name",
        "bank_branch", "bank_swift_code", "bank_notes",
    ], []);

    const _normStr = (v) => (v == null ? "" : String(v).trim());

    const _diffPayload = React.useCallback(() => {
        // Iter65 Phase A — Build the PATCH payload from ONLY the fields
        // the user actually modified. Untouched empty fields are NEVER
        // sent as `null` — that was the prod data-loss root cause.
        //
        // Returns { patch: {...}, bankFieldsBeingCleared: [...] }.
        const initial = initialFormRef.current || {};
        const patch = {};
        const bankFieldsBeingCleared = [];
        for (const [k, v] of Object.entries(form)) {
            const old = initial[k];
            const newN = _normStr(v);
            const oldN = _normStr(old);
            if (newN === oldN) continue;      // untouched → skip
            if (_BANK_FIELDS.includes(k)) {
                // Bank field cleared (had a value, now empty). Recorded
                // for the confirmation dialog + DELETE endpoint call.
                if (oldN && !newN) {
                    bankFieldsBeingCleared.push(k);
                } else if (newN) {
                    patch[k] = v.trim();
                }
                continue;
            }
            // Non-bank field: preserve the "typed-and-deleted → null"
            // semantic. Empty strings normalise to null so the backend
            // strips them (existing safe behaviour).
            patch[k] = newN || null;
        }
        return { patch, bankFieldsBeingCleared };
    }, [form, _BANK_FIELDS]);

    const _doPatchAndSave = React.useCallback(async (
        patch, bankFieldsBeingCleared, extraToastPrefix = ""
    ) => {
        // Iter65 — the actual save transaction. Applies the diff PATCH,
        // then (if bank fields were intentionally cleared) hits the
        // dedicated DELETE endpoint. Wage-rate side-effect stays.
        if (Object.keys(patch).length > 0) {
            await api.patch(`/employees/${employee.id}`, patch);
        }
        if (bankFieldsBeingCleared.length > 0) {
            await api.delete(`/employees/${employee.id}/bank-account`);
        }
        // Wage-rate side-effect (unchanged).
        let extra = "";
        try {
            extra = await _maybeCreatePayRate(employee.id, form);
        } catch (rateErr) {
            console.warn("wage rate side-effect failed on edit:", rateErr);
            toast.warning("Employee updated, but new pay rate wasn't saved. Please add it manually from Wage Rates.");
            onOpenChange(false);
            onSaved?.();
            return;
        }
        const bits = [];
        if (extraToastPrefix) bits.push(extraToastPrefix);
        if (Object.keys(patch).length === 0 && bankFieldsBeingCleared.length === 0) {
            bits.push("No changes to save");
        } else if (bankFieldsBeingCleared.length > 0) {
            bits.push(`Bank account cleared (${bankFieldsBeingCleared.length} field${bankFieldsBeingCleared.length === 1 ? "" : "s"})`);
        } else {
            bits.push("Employee updated");
        }
        if (extra) bits.push(extra);
        toast.success(bits.join(". "));
        onOpenChange(false);
        onSaved?.();
    }, [employee, form, onOpenChange, onSaved]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            if (isEdit) {
                const { patch, bankFieldsBeingCleared } = _diffPayload();
                if (Object.keys(patch).length === 0 && bankFieldsBeingCleared.length === 0) {
                    toast.info("No changes to save");
                    return;
                }
                if (bankFieldsBeingCleared.length > 0) {
                    // Iter65 Phase A — GATE bank clears behind a
                    // typed-CLEAR confirmation dialog. Prevents an
                    // accidental Save from wiping bank data.
                    setBankClearConfirm({
                        open: true,
                        confirmText: "",
                        pending: { patch, bankFieldsBeingCleared },
                    });
                    return;
                }
                await _doPatchAndSave(patch, [], "");
                return;  // _doPatchAndSave handles close + onSaved
            } else {
                // CREATE — send full payload. Iter60.p12 normalisation
                // still applies for null-vs-empty consistency.
                const payload = { ...form };
                payload.email = payload.email?.trim() || null;
                payload.address = payload.address?.trim() || null;
                payload.worksite = payload.worksite?.trim() || null;
                payload.module_employee_id = payload.module_employee_id?.trim() || null;
                payload.photo_url = payload.photo_url || null;
                for (const bk of _BANK_FIELDS) {
                    const v = (payload[bk] || "").trim();
                    payload[bk] = v || null;
                }
                if (payload.employment_type === "casual") payload.worker_subtype = null;
                if (!payload.worker_subtype) payload.worker_subtype = null;
                const { data: created } = await api.post("/employees", payload);
                // Iter18 — Optional pay rate side-effect. IC-only.
                let extra = "";
                try {
                    extra = await _maybeCreatePayRate(created.id, payload);
                } catch (rateErr) {
                    console.warn("wage rate side-effect failed on create:", rateErr);
                    toast.warning("Employee created, but pay rate wasn't saved. Please add it manually from Wage Rates.");
                    onOpenChange(false);
                    onSaved?.();
                    return;
                }
                if (extra) {
                    toast.success(`Employee registered. ${extra}`);
                } else {
                    toast.success("Employee created");
                }
            }
            onOpenChange(false);
            onSaved?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl max-h-[90vh] overflow-y-auto"
                data-testid="employee-form-dialog"
            >
                <DialogHeader>
                    <DialogTitle>{isEdit ? "Edit Employee" : "Add Employee"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={onSubmit} className="space-y-4 pt-2">
                    {/* Casual / Non-Casual segmented control */}
                    <div className="space-y-2" data-testid="emp-type-segment">
                        <Label className="text-xs uppercase tracking-wide text-[color:var(--asl-muted)]">
                            Worker type <span className="asl-crimson-text">*</span>
                        </Label>
                        <div className="grid grid-cols-2 gap-3">
                            <button type="button"
                                onClick={() => { set("employment_type", "casual"); set("worker_subtype", ""); }}
                                data-testid="emp-type-casual"
                                className={`text-left p-4 rounded-lg border-2 transition ${
                                    form.employment_type === "casual"
                                        ? "asl-navy-bg text-white border-transparent"
                                        : "bg-white asl-navy-text hover:border-[color:var(--asl-navy)]"
                                }`}
                                style={{ borderColor: form.employment_type === "casual" ? "transparent" : "var(--asl-border)" }}>
                                <div className="text-sm font-bold uppercase tracking-wide">Casual Worker</div>
                                <div className={`text-xs mt-1 ${form.employment_type === "casual" ? "text-white/80" : "text-[color:var(--asl-muted)]"}`}>
                                    Hourly / Daily wage · weekly or bi-weekly cycle · task-based
                                </div>
                            </button>
                            <button type="button"
                                onClick={() => set("employment_type", "non_casual")}
                                data-testid="emp-type-non-casual"
                                className={`text-left p-4 rounded-lg border-2 transition ${
                                    form.employment_type === "non_casual"
                                        ? "asl-crimson-bg text-white border-transparent"
                                        : "bg-white asl-navy-text hover:asl-crimson-text"
                                }`}
                                style={{ borderColor: form.employment_type === "non_casual" ? "transparent" : "var(--asl-border)" }}>
                                <div className="text-sm font-bold uppercase tracking-wide">Non-Casual Worker</div>
                                <div className={`text-xs mt-1 ${form.employment_type === "non_casual" ? "text-white/80" : "text-[color:var(--asl-muted)]"}`}>
                                    Monthly salary · smart contract (basic + benefits)
                                </div>
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <Avatar src={form.photo_url} name={form.full_name} size={64} />
                        <div>
                            <label
                                className="text-xs asl-navy-text font-medium cursor-pointer underline"
                                data-testid="employee-photo-upload-label"
                            >
                                Upload photo (JPG/PNG, max 2 MB)
                                <input
                                    type="file"
                                    accept="image/png,image/jpeg"
                                    onChange={onPhotoChange}
                                    className="hidden"
                                    data-testid="employee-photo-input"
                                />
                            </label>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Full name" required>
                            <Input
                                required
                                value={form.full_name}
                                onChange={(e) => set("full_name", e.target.value)}
                                data-testid="employee-full-name"
                            />
                        </Field>
                        <Field label="Employee phone" required>
                            <Input
                                required
                                value={form.phone}
                                onChange={(e) => set("phone", e.target.value)}
                                data-testid="employee-phone"
                            />
                        </Field>
                        <Field label="Email">
                            <Input
                                type="email"
                                value={form.email || ""}
                                onChange={(e) => set("email", e.target.value)}
                                data-testid="employee-email"
                            />
                        </Field>
                        <Field label="National ID" required>
                            <Input
                                required
                                value={form.national_id}
                                onChange={(e) => set("national_id", e.target.value)}
                                data-testid="employee-national-id"
                            />
                        </Field>
                        <Field label="Module Employee ID">
                            <Input
                                value={form.module_employee_id || ""}
                                onChange={(e) => set("module_employee_id", e.target.value)}
                                placeholder="e.g. IC-045, APC-1123 (optional)"
                                maxLength={32}
                                data-testid="employee-module-employee-id"
                            />
                            <div className="text-[10px] text-[color:var(--asl-muted)] mt-1">
                                Optional — the employee ID this person has in your module&apos;s existing records.
                            </div>
                        </Field>
                        <Field label="Biometric User ID">
                            <Input
                                value={form.biometric_user_id || ""}
                                onChange={(e) => set("biometric_user_id", e.target.value)}
                                placeholder={isEdit ? "" : "auto-generated from Employee Code"}
                                data-testid="employee-biometric-user-id"
                            />
                            <div className="text-[10px] text-[color:var(--asl-muted)] mt-1">
                                Auto-generated from Employee Code (e.g. AP-1009 → 1009). Only change if this
                                employee is already enrolled on a device with a different USERID.
                            </div>
                        </Field>
                        <Field label="Gender" required>
                            <SelectField
                                value={form.gender}
                                onChange={(v) => set("gender", v)}
                                items={[
                                    { value: "male", label: "Male" },
                                    { value: "female", label: "Female" },
                                    { value: "other", label: "Other" },
                                ]}
                                testid="employee-gender"
                            />
                        </Field>
                        <Field label="Date of birth">
                            <DateInput
                                value={form.date_of_birth}
                                onChange={(v) => set("date_of_birth", v)}
                                data-testid="employee-dob"
                            />
                            <AgeReadout dob={form.date_of_birth} />
                        </Field>
                        <Field label="Module" required>
                            <SelectField
                                value={form.module_id}
                                onChange={(v) => set("module_id", v)}
                                placeholder="Choose module"
                                items={modules.map((m) => ({ value: m.id, label: m.name }))}
                                testid="employee-module"
                            />
                        </Field>
                        <Field label="Worksite">
                            <Input
                                value={form.worksite || ""}
                                onChange={(e) => set("worksite", e.target.value)}
                                data-testid="employee-worksite"
                            />
                        </Field>
                        <Field label="Job title" required>
                            <Input
                                required
                                value={form.job_title}
                                onChange={(e) => set("job_title", e.target.value)}
                                data-testid="employee-job-title"
                            />
                        </Field>
                        <Field label="Hire date" required>
                            <DateInput
                                required
                                value={form.hire_date}
                                onChange={(v) => set("hire_date", v)}
                                data-testid="employee-hire-date"
                            />
                        </Field>
                        <Field label="Probation end date">
                            <DateInput
                                value={form.probation_end_date || ""}
                                onChange={(v) => set("probation_end_date", v)}
                                data-testid="employee-probation-end-date"
                            />
                            <p className="text-[10px] text-[color:var(--asl-muted)] mt-1">
                                Optional. Defaults to hire date + 3 months if empty (config).
                                Sick leave requires probation to have ended.
                            </p>
                        </Field>
                        <Field label="Employment type" required>
                            <div className="text-xs px-3 py-2 rounded asl-navy-bg text-white text-center font-semibold uppercase tracking-wide" data-testid="employee-emp-type-display">
                                {form.employment_type === "casual" ? "Casual" : "Non-Casual"}
                                <span className="ml-2 opacity-70 text-[10px]">(set above)</span>
                            </div>
                        </Field>
                        {form.employment_type === "non_casual" && (
                            <Field label="Worker subtype" required>
                                <SelectField
                                    value={form.worker_subtype}
                                    onChange={(v) => set("worker_subtype", v)}
                                    placeholder="Choose subtype"
                                    items={[
                                        { value: "manual", label: "Manual" },
                                        { value: "non_manual", label: "Non-Manual" },
                                    ]}
                                    testid="employee-subtype"
                                />
                            </Field>
                        )}
                        <Field label="Status">
                            <SelectField
                                value={form.status}
                                onChange={(v) => set("status", v)}
                                items={[
                                    { value: "active", label: "Active" },
                                    { value: "suspended", label: "Suspended" },
                                    { value: "resigned", label: "Resigned" },
                                    { value: "terminated", label: "Terminated" },
                                    { value: "replaced", label: "Replaced" },
                                ]}
                                testid="employee-status"
                            />
                        </Field>
                        {/* Iter60.p59 — Employment lifecycle dropdown. HR
                            can flip this on the row form OR use the
                            dedicated "Change Status" action (which
                            writes to the audit trail more precisely). */}
                        <Field label="Employment Status">
                            <SelectField
                                value={form.employment_status || "active"}
                                onChange={(v) => set("employment_status", v)}
                                items={[
                                    { value: "active", label: "Active" },
                                    { value: "undeployed", label: "Undeployed" },
                                    { value: "project_completed", label: "Project Completed" },
                                    { value: "contract_ended", label: "Contract Ended" },
                                    { value: "resigned", label: "Resigned" },
                                    { value: "terminated", label: "Terminated" },
                                    { value: "retired", label: "Retired" },
                                    { value: "on_leave_extended", label: "On Leave (Extended)" },
                                ]}
                                testid="employee-employment-status"
                            />
                        </Field>
                    </div>
                    {/* Iter60.p59 — Status note. Free-form crumb visible
                        in the audit trail (e.g. "Contract ended, project
                        5050"). Rendered outside the 2-col grid so the
                        row wraps cleanly on narrow viewports. */}
                    <Field label="Status Note">
                        <Input
                            value={form.status_note || ""}
                            onChange={(e) => set("status_note", e.target.value)}
                            placeholder="Optional — reason or context for the current employment status"
                            maxLength={500}
                            data-testid="employee-status-note"
                        />
                    </Field>

                    <Field label="Address">
                        <Textarea
                            value={form.address || ""}
                            onChange={(e) => set("address", e.target.value)}
                            rows={2}
                            data-testid="employee-address"
                        />
                    </Field>

                    {/* Iter18 — Pay Rate section (IC-only, create & edit). Optional.
                        Side-effect: on save, POSTs to /wage-rates with this employee's id.
                        Iter44 — Also available on Edit; blank amount keeps existing rate. */}
                    {showPayRate && (
                        <div className="border rounded-md p-3 space-y-2" style={{ borderColor: "var(--asl-border)", background: "var(--asl-navy-50)" }} data-testid="employee-pay-rate-section">
                            <div className="flex items-center justify-between">
                                <div className="text-xs font-semibold asl-navy-text uppercase tracking-wider">
                                    Pay Rate <span className="text-[color:var(--asl-muted)] font-normal normal-case tracking-normal">(optional)</span>
                                </div>
                                {isEdit && (
                                    <Link
                                        to={`/m/${(modulesById[form.module_id]?.code || "IC").toLowerCase()}/staff/${employee?.id}`}
                                        className="text-[11px] asl-crimson-text hover:underline"
                                        data-testid="employee-payrate-view-history"
                                        onClick={() => onOpenChange(false)}
                                    >
                                        View history →
                                    </Link>
                                )}
                            </div>
                            {isEdit && (
                                <div className="rounded-md border bg-white px-3 py-2 text-xs" style={{ borderColor: "var(--asl-border)" }} data-testid="employee-payrate-current">
                                    {currentRateLoading ? (
                                        <span className="text-[color:var(--asl-muted)] italic">Loading current rate…</span>
                                    ) : currentRate?.amount ? (
                                        <span>
                                            <span className="text-[color:var(--asl-muted)]">Current:</span>{" "}
                                            <span className="font-semibold asl-navy-text mono" data-testid="employee-payrate-current-amount">
                                                ${Number(currentRate.amount).toFixed(2)} / {currentRate.frequency}
                                            </span>
                                            <span className="text-[color:var(--asl-muted)]">
                                                {" · "}
                                                {currentRate.source === "employee_specific" ? "Person-specific" : "Role-based"}
                                                {currentRate.effective_date ? ` · effective from ${formatDate(currentRate.effective_date)}` : ""}
                                            </span>
                                        </span>
                                    ) : (
                                        <span className="text-[color:var(--asl-muted)] italic" data-testid="employee-payrate-current-none">No pay rate assigned yet.</span>
                                    )}
                                </div>
                            )}
                            <div className="grid grid-cols-3 gap-3">
                                <Field label="Amount ($)">
                                    <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={payRate.amount}
                                        onChange={(e) => setPayRate((p) => ({ ...p, amount: e.target.value }))}
                                        placeholder="0.00"
                                        data-testid="employee-payrate-amount"
                                    />
                                </Field>
                                <Field label="Frequency">
                                    <SelectField
                                        value={payRate.frequency || (form.employment_type === "casual" ? "daily" : "monthly")}
                                        onChange={(v) => setPayRate((p) => ({ ...p, frequency: v }))}
                                        items={[
                                            { value: "hourly", label: "Hourly" },
                                            { value: "daily", label: "Daily" },
                                            { value: "monthly", label: "Monthly" },
                                        ]}
                                        testid="employee-payrate-frequency"
                                    />
                                </Field>
                                <Field label="Effective From">
                                    <DateInput
                                        value={payRate.effective_date || form.hire_date || ""}
                                        onChange={(v) => setPayRate((p) => ({ ...p, effective_date: v }))}
                                        data-testid="employee-payrate-effective"
                                    />
                                </Field>
                            </div>
                            <div className="text-[11px] text-[color:var(--asl-muted)] leading-relaxed">
                                {isEdit
                                    ? "Leave blank to keep the current rate. Fill in a new amount + effective date to add a new rate — the old rate remains in history for audit."
                                    : "Optional. If left blank, this employee will use the role-based rate defaults for their job. You can always change or add rates later from the Wage Rates page."}
                            </div>
                        </div>
                    )}

                    {/* Iter54 — Government Documents section */}
                    <div className="rounded-lg border border-[color:var(--asl-border)] p-4 space-y-3" data-testid="gov-docs-section">
                        <div className="flex items-baseline justify-between">
                            <h4 className="text-sm font-bold asl-navy-text">Government Documents</h4>
                            <span className="text-[11px] text-[color:var(--asl-muted)]">Optional — helps with compliance & audit</span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs">CID Reference <span className="text-[color:var(--asl-muted)]">(Somali Police Security Clearance)</span></Label>
                                <Input value={form.cid_reference} onChange={(e) => set("cid_reference", e.target.value)}
                                    placeholder="SPF security clearance ref" data-testid="cid-reference" />
                            </div>
                            <div>
                                <Label className="text-xs">CID Date of Issue</Label>
                                <DateInput value={form.cid_date_of_issue} onChange={(v) => set("cid_date_of_issue", v)} data-testid="cid-date-of-issue" />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-[color:var(--asl-border)]">
                            <div>
                                <Label className="text-xs">NID Reference <span className="text-[color:var(--asl-muted)]">(National ID)</span></Label>
                                <Input value={form.nid_reference} onChange={(e) => set("nid_reference", e.target.value)}
                                    placeholder="Somali National ID number" data-testid="nid-reference" />
                            </div>
                            <div>
                                <Label className="text-xs">NID Date of Issue</Label>
                                <DateInput value={form.nid_date_of_issue} onChange={(v) => set("nid_date_of_issue", v)} data-testid="nid-date-of-issue" />
                            </div>
                            <div>
                                <Label className="text-xs">NID Expiry Date</Label>
                                <DateInput value={form.nid_expiry_date} onChange={(v) => set("nid_expiry_date", v)} data-testid="nid-expiry-date" />
                            </div>
                        </div>
                    </div>

                    {/* Iter54 — ASL Company ID Card section */}
                    <div className="rounded-lg border border-[color:var(--asl-border)] p-4 space-y-3" data-testid="asl-id-card-section">
                        <div className="flex items-baseline justify-between">
                            <h4 className="text-sm font-bold asl-navy-text">ASL Company ID Card</h4>
                            <IdCardStatusBadge expiry={form.asl_id_card_expiry_date} />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs">Issue Date</Label>
                                <DateInput value={form.asl_id_card_issue_date} onChange={(v) => set("asl_id_card_issue_date", v)} data-testid="asl-card-issue-date" />
                            </div>
                            <div>
                                <Label className="text-xs">Expiry Date</Label>
                                <DateInput value={form.asl_id_card_expiry_date} onChange={(v) => set("asl_id_card_expiry_date", v)} data-testid="asl-card-expiry-date" />
                            </div>
                        </div>
                        <IdCardDaysRemaining expiry={form.asl_id_card_expiry_date} />
                    </div>

                    {/* Iter60.p12 — Bank Account Details (collapsible, optional).
                        Closed by default. If any field is populated on the loaded
                        employee, we auto-expand so the user isn't surprised the
                        section is "hidden". */}
                    <BankAccountAccordion form={form} set={set} isEdit={isEdit} />

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            data-testid="employee-form-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="asl-navy-bg text-white"
                            disabled={submitting}
                            data-testid="employee-form-submit"
                            data-iter65="employee-save-dirty-fields-only"
                        >
                            {submitting ? "Saving…" : isEdit
                                ? "Save changes"
                                : form.employment_type === "casual"
                                    ? "Register Casual Worker"
                                    : "Register Non-Casual Worker"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
            {/* Iter65 Phase A — Bank-clear confirmation. Shown only when
                a Save would blank previously-populated bank fields. */}
            <Dialog
                open={bankClearConfirm.open}
                onOpenChange={(v) => !v && setBankClearConfirm({
                    open: false, confirmText: "", pending: null,
                })}
            >
                <DialogContent data-testid="bank-clear-confirm-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-[#A8324A]">
                            Clear {employee?.full_name}'s bank account details?
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        <p>
                            You're about to clear{" "}
                            <b>{bankClearConfirm.pending?.bankFieldsBeingCleared.length}</b>{" "}
                            bank field
                            {bankClearConfirm.pending?.bankFieldsBeingCleared.length === 1 ? "" : "s"}
                            {" "}on this employee. This action is logged
                            to the audit trail and cannot be undone from
                            the UI.
                        </p>
                        <ul className="text-xs text-neutral-600 list-disc pl-5">
                            {bankClearConfirm.pending?.bankFieldsBeingCleared.map((f) => (
                                <li key={f}>
                                    {f.replace(/_/g, " ")}
                                </li>
                            ))}
                        </ul>
                        <label className="block text-xs font-semibold text-neutral-700">
                            Type <span className="font-mono">CLEAR</span> to confirm:
                        </label>
                        <input
                            type="text"
                            autoFocus
                            className="w-full border rounded px-2 py-1 text-sm"
                            value={bankClearConfirm.confirmText}
                            onChange={(e) => setBankClearConfirm((s) => ({
                                ...s, confirmText: e.target.value,
                            }))}
                            data-testid="bank-clear-confirm-input"
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setBankClearConfirm({
                                open: false, confirmText: "", pending: null,
                            })}
                            data-testid="bank-clear-confirm-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            disabled={bankClearConfirm.confirmText.trim() !== "CLEAR" || submitting}
                            className="bg-[#A8324A] text-white hover:bg-[#8f2a3e]"
                            data-testid="bank-clear-confirm-submit"
                            onClick={async () => {
                                const p = bankClearConfirm.pending;
                                if (!p) return;
                                setBankClearConfirm({
                                    open: false, confirmText: "", pending: null,
                                });
                                try {
                                    await _doPatchAndSave(
                                        p.patch, p.bankFieldsBeingCleared, "",
                                    );
                                } catch (err) {
                                    toast.error(formatApiError(err));
                                } finally {
                                    setSubmitting(false);
                                }
                            }}
                        >
                            Clear bank account
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Dialog>
    );
}

function Field({ label, required, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-[color:var(--asl-muted)]">
                {label} {required && <span className="asl-crimson-text">*</span>}
            </Label>
            {children}
        </div>
    );
}

function SelectField({ value, onChange, items, placeholder, testid }) {
    return (
        <Select value={value || undefined} onValueChange={onChange}>
            <SelectTrigger data-testid={testid}>
                <SelectValue placeholder={placeholder || "Select"} />
            </SelectTrigger>
            <SelectContent>
                {items.map((i) => (
                    <SelectItem key={i.value} value={i.value} data-testid={`${testid}-opt-${i.value}`}>
                        {i.label}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}


/**
 * Iter54 — Live-compute helpers.
 *   AgeReadout — from DOB.
 *   IdCardStatusBadge — from expiry date (matches backend thresholds:
 *     >30d Valid, 0–30d Expiring soon, ≤0d Expired, no date Unknown).
 *   IdCardDaysRemaining — count of days remaining (or elapsed since expiry).
 */
function AgeReadout({ dob }) {
    if (!dob) return null;
    let age = null;
    try {
        const b = new Date(dob + "T00:00:00");
        const now = new Date();
        age = now.getFullYear() - b.getFullYear();
        const m = now.getMonth() - b.getMonth();
        if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age -= 1;
    } catch { return null; }
    if (age == null || Number.isNaN(age)) return null;
    return <div className="text-[11px] text-[color:var(--asl-muted)] mono mt-1" data-testid="employee-age">Age: {age}</div>;
}


function IdCardStatusBadge({ expiry }) {
    if (!expiry) return <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500" data-testid="asl-card-status">Unknown</span>;
    let days;
    try {
        const e = new Date(expiry + "T00:00:00");
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        days = Math.floor((e - now) / (24 * 3600 * 1000));
    } catch {
        return <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500" data-testid="asl-card-status">Unknown</span>;
    }
    if (days > 30) return <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800" data-testid="asl-card-status">Valid</span>;
    if (days > 0)  return <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid="asl-card-status">Expiring soon</span>;
    return <span className="text-[11px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-800" data-testid="asl-card-status">Expired</span>;
}

function IdCardDaysRemaining({ expiry }) {
    if (!expiry) return null;
    let days;
    try {
        const e = new Date(expiry + "T00:00:00");
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        days = Math.floor((e - now) / (24 * 3600 * 1000));
    } catch { return null; }
    const label = days > 0 ? `${days} day(s) remaining` : `expired ${Math.abs(days)} day(s) ago`;
    return (
        <div className="text-xs text-[color:var(--asl-muted)]" data-testid="asl-card-days-remaining">
            <span className="mono">{label}</span>
        </div>
    );
}


/**
 * Iter60.p12 — Collapsible Bank Account Details section.
 *
 * Collapsed by default on new/empty rows; auto-expanded when the loaded
 * employee already has any bank field populated (so the user isn't
 * confused why the section is "missing").
 *
 * Partial-data guard is enforced server-side (400 → `bank_partial_data`);
 * we display a lightweight inline hint when the user has entered any
 * field but is missing bank_name or bank_account_number.
 */
function BankAccountAccordion({ form, set }) {
    const hasAnyBank = React.useMemo(
        () => Boolean(
            (form.bank_name || form.bank_account_number || form.bank_account_holder_name
                || form.bank_branch || form.bank_swift_code || form.bank_notes)?.toString().trim()
        ),
        [form.bank_name, form.bank_account_number, form.bank_account_holder_name,
         form.bank_branch, form.bank_swift_code, form.bank_notes],
    );
    // Start open when there's existing data (edit mode with populated bank).
    const [open, setOpen] = React.useState(hasAnyBank);
    // Keep expanded state in sync if the form flips from empty→populated
    // (happens once when an existing employee's data hydrates the form).
    React.useEffect(() => {
        if (hasAnyBank) setOpen(true);
    }, [hasAnyBank]);

    const partialWarn = hasAnyBank && !(form.bank_name && form.bank_account_number);
    const complete = Boolean(form.bank_name && form.bank_account_number);

    return (
        <Collapsible
            open={open}
            onOpenChange={setOpen}
            className="rounded-lg border border-[color:var(--asl-border)]"
            data-testid="bank-details-section"
        >
            <CollapsibleTrigger asChild>
                <button
                    type="button"
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 rounded-lg"
                    data-testid="bank-details-toggle"
                    aria-expanded={open}
                >
                    <span className="flex items-center gap-2">
                        <Landmark className="h-4 w-4 asl-navy-text" aria-hidden="true" />
                        <span className="text-sm font-bold asl-navy-text">Bank Account Details</span>
                        <span className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">Optional</span>
                        {complete && (
                            <span
                                className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800"
                                data-testid="bank-details-status-complete"
                            >
                                Set
                            </span>
                        )}
                        {partialWarn && (
                            <span
                                className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800"
                                data-testid="bank-details-status-partial"
                                title="Incomplete: bank name + account number are required."
                            >
                                Incomplete
                            </span>
                        )}
                    </span>
                    <ChevronDown
                        className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
                        aria-hidden="true"
                    />
                </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="px-4 pb-4 space-y-3">
                <div className="text-[11px] text-[color:var(--asl-muted)]">
                    Used for payroll wire transfers. If any field is set, Bank Name + Account Number are required.
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Bank Name">
                        <Input
                            value={form.bank_name}
                            onChange={(e) => set("bank_name", e.target.value)}
                            maxLength={120}
                            data-testid="bank-name"
                            placeholder="e.g. Salaam Somali Bank"
                        />
                    </Field>
                    <Field label="Account Number">
                        <Input
                            value={form.bank_account_number}
                            onChange={(e) => set("bank_account_number", e.target.value.slice(0, 40))}
                            maxLength={40}
                            className="mono"
                            data-testid="bank-account-number"
                            placeholder="Digits or IBAN"
                        />
                    </Field>
                    <Field label="Account Holder Name">
                        <Input
                            value={form.bank_account_holder_name}
                            onChange={(e) => set("bank_account_holder_name", e.target.value)}
                            maxLength={120}
                            data-testid="bank-account-holder-name"
                            placeholder="Defaults to employee full name if empty"
                        />
                    </Field>
                    <Field label="Branch">
                        <Input
                            value={form.bank_branch}
                            onChange={(e) => set("bank_branch", e.target.value)}
                            maxLength={120}
                            data-testid="bank-branch"
                            placeholder="Optional"
                        />
                    </Field>
                    <Field label="SWIFT / BIC Code">
                        <Input
                            value={form.bank_swift_code}
                            onChange={(e) => set("bank_swift_code", e.target.value.toUpperCase().slice(0, 32))}
                            maxLength={32}
                            className="mono uppercase"
                            data-testid="bank-swift-code"
                            placeholder="Optional"
                        />
                    </Field>
                    <div className="md:col-span-2">
                        <Field label="Notes">
                            <Textarea
                                rows={2}
                                value={form.bank_notes}
                                onChange={(e) => set("bank_notes", e.target.value)}
                                maxLength={500}
                                data-testid="bank-notes"
                                placeholder="Internal notes for the payroll team (optional)"
                            />
                        </Field>
                    </div>
                </div>
                {partialWarn && (
                    <div
                        className="text-[11px] px-3 py-2 rounded bg-amber-50 border border-amber-200 text-amber-900"
                        data-testid="bank-partial-warning"
                    >
                        Bank Name and Account Number are both required when any bank field is set.
                    </div>
                )}
            </CollapsibleContent>
        </Collapsible>
    );
}
